import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormInitContextType } from '../types/context';
import { useRouteTransitionContext } from '../hooks/context/useRouteTransitionContext';

const defaultValues: FormInitContextType = {
  isProvided: false,
  ready: false,
  register: () => () => {},
};

export const FormInitContext =
  createContext<FormInitContextType>(defaultValues);

const FormInitProvider = ({
  children,
  onInitialized,
}: {
  children: ReactNode;
  onInitialized?: () => void;
}) => {
  const parentCtx = useContext(FormInitContext);
  const { requestOverlay, releaseOverlay } = useRouteTransitionContext();
  const parentReady = !parentCtx.isProvided || parentCtx.ready;

  const pendingCount = useRef(0);
  const [initialized, setInitialized] = useState(false);
  const onInitializedRef = useRef(onInitialized);
  onInitializedRef.current = onInitialized;

  const register = useCallback((): (() => void) => {
    pendingCount.current += 1;
    let done = false;
    // Returns markReady — caller must invoke it exactly once when init completes.
    return () => {
      if (done) return;
      done = true;
      pendingCount.current -= 1;
      if (pendingCount.current === 0) {
        setInitialized(true);
      }
    };
  }, []);

  // Children effects run before parent effects — if pendingCount is still 0 here,
  // nothing registered (all non-async visibility), so initialization is complete.
  useEffect(() => {
    if (pendingCount.current === 0) {
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (initialized && parentReady) {
      onInitializedRef.current?.();
    }
  }, [initialized, parentReady]);

  // Drive the shared PageLayout overlay while initializing instead of rendering
  // an inline spinner. Ref-counted, so nested sections compose.
  const showOverlay = parentReady && !initialized;

  useEffect(() => {
    if (!showOverlay) {
      return;
    }
    requestOverlay();
    return releaseOverlay;
  }, [showOverlay, requestOverlay, releaseOverlay]);

  const value = useMemo<FormInitContextType>(
    () => ({ isProvided: true, register, ready: initialized && parentReady }),
    [register, initialized, parentReady]
  );

  return (
    <FormInitContext.Provider value={value}>
      <div
        style={{ display: parentReady && initialized ? 'contents' : 'none' }}
      >
        {children}
      </div>
    </FormInitContext.Provider>
  );
};

export default FormInitProvider;
